/**
 * WordArt preset library — PowerPoint / Google Slides style text effects.
 */

export const WORD_ART_PRESETS = {
  'gradient-ocean': {
    label: 'Ocean Gradient',
    previewText: 'Aa',
    defaultText: 'Ocean Wave',
    effects: {
      fillType: 'gradient',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #06beb6 100%)',
      fontFamily: 'Georgia, serif',
      fontSize: '48px',
      fontWeight: '700',
      letterSpacing: '1px',
      shadow: '0 4px 12px rgba(102,126,234,0.4)',
    },
  },
  'gradient-sunset': {
    label: 'Sunset Gradient',
    previewText: 'Aa',
    defaultText: 'Sunset Glow',
    effects: {
      fillType: 'gradient',
      gradient: 'linear-gradient(90deg, #f12711 0%, #f5af19 50%, #ff6b6b 100%)',
      fontFamily: 'Impact, sans-serif',
      fontSize: '52px',
      fontWeight: '700',
      letterSpacing: '2px',
      shadow: '0 6px 16px rgba(245,175,25,0.5)',
    },
  },
  'gradient-rainbow': {
    label: 'Rainbow Fill',
    previewText: 'Aa',
    defaultText: 'Rainbow',
    effects: {
      fillType: 'gradient',
      gradient: 'linear-gradient(90deg, #ff0000, #ff8800, #ffff00, #00cc00, #0088ff, #8800ff)',
      fontFamily: 'Arial Black, sans-serif',
      fontSize: '48px',
      fontWeight: '900',
      letterSpacing: '3px',
    },
  },
  'gradient-gold': {
    label: 'Gold Metallic',
    previewText: 'Aa',
    defaultText: 'Premium',
    effects: {
      fillType: 'gradient',
      gradient: 'linear-gradient(180deg, #fceabb 0%, #f8b500 40%, #b8860b 70%, #fceabb 100%)',
      fontFamily: 'Times New Roman, serif',
      fontSize: '50px',
      fontWeight: '700',
      letterSpacing: '1px',
      shadow: '0 2px 4px rgba(0,0,0,0.3), 0 6px 12px rgba(184,134,11,0.4)',
    },
  },
  'outline-bold': {
    label: 'Bold Outline',
    previewText: 'Aa',
    defaultText: 'Outline',
    effects: {
      fillType: 'solid',
      fillColor: 'transparent',
      stroke: { color: '#1a73e8', width: '3px' },
      fontFamily: 'Arial Black, sans-serif',
      fontSize: '52px',
      fontWeight: '900',
      letterSpacing: '2px',
    },
  },
  'outline-white-shadow': {
    label: 'White Outline + Shadow',
    previewText: 'Aa',
    defaultText: 'Shadow Text',
    effects: {
      fillType: 'solid',
      fillColor: '#ffffff',
      stroke: { color: '#202124', width: '2px' },
      fontFamily: 'Roboto, sans-serif',
      fontSize: '48px',
      fontWeight: '700',
      shadow: '4px 4px 0 #4285f4, 8px 8px 16px rgba(0,0,0,0.25)',
    },
  },
  'shadow-deep': {
    label: 'Deep Shadow',
    previewText: 'Aa',
    defaultText: 'Deep Shadow',
    effects: {
      fillType: 'solid',
      fillColor: '#202124',
      fontFamily: 'Roboto, sans-serif',
      fontSize: '48px',
      fontWeight: '700',
      shadow: '3px 3px 0 #9aa0a6, 6px 6px 0 #80868b, 9px 9px 20px rgba(0,0,0,0.3)',
    },
  },
  'glow-neon': {
    label: 'Neon Glow',
    previewText: 'Aa',
    defaultText: 'NEON',
    effects: {
      fillType: 'solid',
      fillColor: '#ffffff',
      fontFamily: 'Arial Black, sans-serif',
      fontSize: '48px',
      fontWeight: '900',
      letterSpacing: '4px',
      textTransform: 'uppercase',
      glow: { color: '#00ffff', size: '20px' },
    },
  },
  'glow-fire': {
    label: 'Fire Glow',
    previewText: 'Aa',
    defaultText: 'FIRE',
    effects: {
      fillType: 'gradient',
      gradient: 'linear-gradient(180deg, #ffff00 0%, #ff6600 50%, #cc0000 100%)',
      fontFamily: 'Impact, sans-serif',
      fontSize: '52px',
      fontWeight: '900',
      letterSpacing: '3px',
      textTransform: 'uppercase',
      glow: { color: '#ff4400', size: '16px' },
      shadow: '0 4px 8px rgba(204,0,0,0.5)',
    },
  },
  reflection: {
    label: 'Reflection',
    previewText: 'Aa',
    defaultText: 'Reflect',
    effects: {
      fillType: 'gradient',
      gradient: 'linear-gradient(180deg, #4285f4 0%, #1a73e8 100%)',
      fontFamily: 'Roboto, sans-serif',
      fontSize: '48px',
      fontWeight: '700',
      reflection: { opacity: 0.35, distance: '4px', blur: '2px' },
    },
  },
  'emboss-3d': {
    label: '3D Emboss',
    previewText: 'Aa',
    defaultText: '3D Text',
    effects: {
      fillType: 'solid',
      fillColor: '#e8eaed',
      fontFamily: 'Arial Black, sans-serif',
      fontSize: '50px',
      fontWeight: '900',
      shadow: '1px 1px 0 #bdc1c6, 2px 2px 0 #9aa0a6, 3px 3px 0 #80868b, 4px 4px 0 #5f6368, 5px 5px 10px rgba(0,0,0,0.3)',
    },
  },
  'perspective-tilt': {
    label: 'Perspective Tilt',
    previewText: 'Aa',
    defaultText: 'Perspective',
    effects: {
      fillType: 'gradient',
      gradient: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
      fontFamily: 'Roboto, sans-serif',
      fontSize: '46px',
      fontWeight: '700',
      transform3d: 'perspective(400px) rotateX(15deg) rotateY(-8deg)',
      shadow: '0 12px 24px rgba(161,140,209,0.4)',
    },
  },
};

export const createWordArtTextObject = (presetId) => {
  const preset = WORD_ART_PRESETS[presetId];
  if (!preset) {
    throw new Error(`Unknown WordArt preset: ${presetId}`);
  }

  return {
    delta: {
      ops: [{ insert: preset.defaultText }, { insert: '\n' }],
    },
    effects: {
      presetId,
      ...preset.effects,
    },
  };
};
